import React, { FC, useState } from 'react';
import { Drawer, Spin } from 'antd';
import { LoadingOutlined } from '@ant-design/icons';
import Titlebar from '../../../../../../clients/sidebar/views/SidebarPanel/Detailbar/Titlebar';
import Title from '../../../../../../clients/sidebar/views/SidebarPanel/Detailbar/Title';
import { TaskDescriptionField } from '../../../../../../clients/sidebar/components/RichTextDescription/TaskDescriptionField';
import TaskActions from '../../../../../../clients/sidebar/views/SidebarPanel/Detailbar/TaskActions';
import ReportedBy from '../../../../../../clients/sidebar/views/SidebarPanel/Detailbar/ReportedBy';
import { OrganizationProvider } from '../../../../../../clients/providers/Organization';
import { CurrentUserProvider } from '../../../../../../clients/providers/CurrentUser';
import { ProjectProvider } from '../../../../../../clients/providers/Project';
import { PriorityId } from '../../../../../../clients/sidebar/models/Task';
import { DashboardTask } from './queries';
import { useProjectContext } from './useProjectContext';
import { useTaskMutations } from './useTaskMutations';
import { getTaskColumn } from './getTaskColumn';
import { DrawerTabs } from './DrawerTabs';
import { mapMentionableTasks } from './mapMentionableTasks';
import { DrawerLinkedTasks } from './DrawerLinkedTasks';
import { DrawerSkeleton } from './DrawerSkeleton';
import { parseDashboardTaskJson } from './parseDashboardTaskJson';
import styles from './styles.module.css';
import cx from 'classnames';

export type PreviewedTaskInfo = {
  id?: string;
  apiKey?: string;
};

type Props = {
  task: DashboardTask;
  taskLoading: boolean;
  isSwitchingTask?: boolean;
  bugherdUrl: string;
  getPopupContainer: () => HTMLElement;
  onClose: () => void;
  refetchTask: () => void;
  currentUserId: string;
  currentUserRole: string;
  onOpenTask?: (taskId: number) => void;
  showTaskNav?: { next: boolean; previous: boolean };
  onTaskChange?: (next?: boolean) => void;
};

export const TaskDetailsDrawer: FC<Props> = ({
  task: rawTask,
  taskLoading,
  isSwitchingTask,
  bugherdUrl,
  getPopupContainer,
  onClose,
  refetchTask,
  currentUserId,
  currentUserRole,
  onOpenTask,
  showTaskNav,
  onTaskChange
}) => {
  const task = parseDashboardTaskJson(rawTask);
  const {
    projectContext,
    accessTo,
    loading: projectContextLoading
  } = useProjectContext({
    projectId: task.project.id,
    apiKey: task.project.apiKey,
    organizationId: task.project.organizationId
  });
  const {
    updateTask,
    moveTaskToProject,
    deleteTask,
    createTag
  } = useTaskMutations({
    task,
    projectContext,
    bugherdUrl,
    refetchTask,
    onDeleted: onClose
  });
  const [open, setOpen] = useState(true);
  const handleClose = () => {
    setOpen(false);
    setTimeout(onClose, 300);
  };

  const users = (projectContext?.users || []).map(user => ({
    ...user,
    displayName: user.name,
    confirmed: true,
    accountConfirmed: true
  }));
  const usersNotOnProject = (projectContext?.usersNotOnProject || []).map(
    user => ({
      ...user,
      displayName: user.name,
      confirmed: true,
      accountConfirmed: true
    })
  );
  const columns = (projectContext?.columns || []).map(column => ({
    ...column,
    id: Number(column.id),
    projectId: Number(column.projectId),
    columnId: Number(column.id),
    position: Number(column.position || 0),
    isComplete: !!column.complete,
    statusId: column.statusId ? Number(column.statusId) : null
  }));
  const column = getTaskColumn({ task, projectContext });
  const mentionableTasks = mapMentionableTasks(
    task.referencedTasks || [],
    task,
    bugherdUrl
  );

  return (
    <Drawer
      open={open}
      closeIcon={null}
      mask={false}
      width={430}
      footer={null}
      getContainer={getPopupContainer}
      styles={{
        body: {
          display: 'flex',
          flexDirection: 'column',
          padding: 0,
          position: 'relative'
        }
      }}
    >
      <CurrentUserProvider apiDomain={bugherdUrl}>
        <ProjectProvider
          key={task.project.id}
          apiDomain={bugherdUrl}
          apiKey={task.project.apiKey}
          id={String(task.project.id)}
        >
          <Titlebar
            showListbar={() => {}}
            closePanel={handleClose}
            bugherdUrl={bugherdUrl}
            projectId={Number(task.project.id)}
            localTaskId={Number(task.localTaskId)}
            priorityId={Number(task.priorityId || 0) as PriorityId}
            viewKanban
            statusName={
              task.statusName === 'archive' ? 'archived' : task.statusName
            }
            sidebarViewStatuses={!!accessTo?.sidebarViewStatuses}
            showTaskNav={showTaskNav}
            onTaskChange={onTaskChange}
            projectName={projectContext?.name || task.project.name}
            lightSeverityText
          />
          {isSwitchingTask ? (
            <DrawerSkeleton />
          ) : (
            <>
              <div className={styles.detailsHeader}>
                <Title
                  title={task.title || null}
                  canEditTitle
                  updateTitle={(title: string) => updateTask({ title })}
                  projectId={Number(task.project.id)}
                  taskId={Number(task.id)}
                  description={task.description}
                  canUseAiSuggestedTitles={false}
                  isSidebar={false}
                  aiSuggestedTitle={false}
                />
                <TaskDescriptionField
                  value={task.description}
                  onChange={(description: string) =>
                    updateTask({ description })
                  }
                  mode="details"
                  editable
                  fixedWidthDetailsToolbarColumn
                  getContainer={getPopupContainer}
                  mentionPreviewContext={{
                    descriptionMarkdown: task.description,
                    projectMembers: users
                      .filter(user => user.role !== 'guest')
                      .map(user => ({
                        id: Number(user.id),
                        name: user.name,
                        email: user.email
                      })),
                    projectUsers: users.map(user => ({
                      id: Number(user.id),
                      name: user.name,
                      email: user.email
                    })),
                    allUsers: [...users, ...usersNotOnProject].map(user => ({
                      id: Number(user.id),
                      name: user.name,
                      email: user.email
                    })),
                    includeGuests:
                      !!accessTo?.assignGuestsAllowed &&
                      !!projectContext?.assignGuests,
                    mentionableTasks,
                    setActiveTask: onOpenTask,
                    bugherdUrl,
                    projectId: Number(task.project.id)
                  }}
                />
                {accessTo && (
                  <OrganizationProvider
                    id={Number(task.project.organizationId)}
                    apiDomain={bugherdUrl}
                  >
                    <TaskActions
                      taskMoveToProject={accessTo.taskMoveToProject}
                      taskDelete={accessTo.taskDelete}
                      taskEditStatus={accessTo.taskEditStatus}
                      container={getPopupContainer() as HTMLDivElement}
                      columns={columns as any}
                      column={column as any}
                      projectId={Number(task.project.id)}
                      shareUrl={`${bugherdUrl}/t/${task.secretId}`}
                      updateTask={updateTask as any}
                      moveToProject={moveTaskToProject}
                      deleteTask={deleteTask}
                      isAdminView
                    />
                  </OrganizationProvider>
                )}
                {task.requester && (
                  <ReportedBy
                    createdAt={task.createdAt}
                    email={task.requester.email || undefined}
                    display={task.requester.name || ''}
                  />
                )}
                <DrawerLinkedTasks
                  task={task}
                  bugherdUrl={bugherdUrl}
                  container={getPopupContainer() as HTMLDivElement}
                  currentUserRole={currentUserRole}
                  accessTo={accessTo}
                  onOpenTask={onOpenTask}
                />
              </div>
              <div className={styles.detailsBody}>
                <DrawerTabs
                  task={task}
                  bugherdUrl={bugherdUrl}
                  container={getPopupContainer()}
                  currentUserId={currentUserId}
                  currentUserRole={currentUserRole}
                  projectContext={projectContext}
                  accessTo={accessTo}
                  updateTask={updateTask}
                  createTag={createTag}
                  refetchTask={refetchTask}
                  mentionableTasks={mentionableTasks}
                  onOpenTask={onOpenTask}
                />
              </div>
              <div
                className={cx(styles.loadingOverlay, {
                  [styles.activeLoadingOverlay]:
                    taskLoading || projectContextLoading
                })}
              >
                <div className={styles.loadingContent}>
                  <Spin
                    indicator={
                      <LoadingOutlined spin style={{ fontSize: 48 }} />
                    }
                  />
                  <span className={styles.loadingLabel}>Loading...</span>
                </div>
              </div>
            </>
          )}
        </ProjectProvider>
      </CurrentUserProvider>
    </Drawer>
  );
};
